import moment from 'moment';
import { RISK_STATUS, MILESTONE_STATUS, OUTCOME_STATUS, ReportFrequency } from 'domain/shared/newProject';

export const formatDisplayValue = (value: string | undefined): string => {
  if (!value) return '';

  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

const CYCLE_DAYS: Record<string, number> = {
  [ReportFrequency.BIWEEKLY]: 14,
  [ReportFrequency.WEEKLY]: 7,
};

type NullableDate = string | null | undefined;

interface ReportMilestone {
  status?: string;
  completionDate?: NullableDate;
  [key: string]: unknown;
}

interface ReportBusinessOutcome {
  status?: string;
  updatedAt?: NullableDate;
  [key: string]: unknown;
}

interface ReportRisk {
  status?: string;
  updatedAt?: NullableDate;
  [key: string]: unknown;
}

interface FilterableReportData {
  milestones?: ReportMilestone[];
  businessOutcomes?: ReportBusinessOutcome[];
  risks?: ReportRisk[];
  [key: string]: unknown;
}

const inPreviousOrCurrentCycle = (
  dateStr: string | null | undefined,
  cycleDays: number,
  projectStart?: string | null,
): boolean => {
  if (!dateStr) return false;
  const date = moment(dateStr).startOf('day');
  const today = moment().startOf('day');
  const parsed = projectStart ? moment(projectStart).startOf('day') : null;
  const start = parsed?.isValid() && !today.isBefore(parsed) ? parsed : today;

  const fullCycles = Math.floor(today.diff(start, 'days') / cycleDays);
  if (fullCycles <= 0) return date.isSameOrAfter(start, 'day') && !date.isAfter(today, 'day');

  const cycleStart = start.clone().add((fullCycles - 1) * cycleDays, 'days');
  return date.isSameOrAfter(cycleStart, 'day') && !date.isAfter(today, 'day');
};

export const filterReportData = <T extends FilterableReportData | null | undefined>(
  data: T,
  reportFrequency?: string,
  projectStartDate?: string | null,
): T => {
  if (!data) return data;
  const cycleDays = CYCLE_DAYS[reportFrequency ?? ''] ?? CYCLE_DAYS[ReportFrequency.WEEKLY];
  const inReportPeriod = (date: NullableDate) => inPreviousOrCurrentCycle(date, cycleDays, projectStartDate);

  return {
    ...data,
    milestones: (data.milestones ?? []).filter(
      m =>
        m.status !== MILESTONE_STATUS.REJECTED &&
        (m.status === MILESTONE_STATUS.ACTIVE ||
          (m.status === MILESTONE_STATUS.COMPLETED && inReportPeriod(m.completionDate))),
    ),
    businessOutcomes: (data.businessOutcomes ?? []).filter(
      bo =>
        bo.status === OUTCOME_STATUS.ACTIVE || (bo.status === OUTCOME_STATUS.COMPLETED && inReportPeriod(bo.updatedAt)),
    ),
    risks: (data.risks ?? []).filter(r => {
      if (r.status === RISK_STATUS.INACTIVE && !inReportPeriod(r.updatedAt)) {
        return false;
      }
      return true;
    }),
  } as T;
};
